// Today — the first screen and, on most days, the only one.
//
// On the clock: the LiveCard with the elapsed clock (the largest thing on
// screen — spec §6), Clock out, then "Next up". Not on the clock: ONE job
// carries the day's Clock in and the rest are quiet tappable cards. Both
// lists open the job screen.
//
// ── Why one action and not one per job (2026-09-12) ─────────────────────────
//
// Measured in Chromium at 390x844, twelve jobs seeded for the company's today,
// signed in as the crew member they belong to:
//
//     full-width green CLOCK IN buttons           12
//     job cards fully above the fold               3 of 12
//     page / viewport                           3058 / 844  =  3.6 screenfuls
//     at 320px                                  3304 / 844  =  3.9 screenfuls
//     median height of one card + its button     193px (213px at 320px)
//
// A crew member can be clocked into exactly ONE job at a time — routes/field.js
// refuses a second open entry — so twelve equally loud buttons were twelve
// decisions where the app owed them one, and half the screen was buttons.
// Nobody loses a job: every other card opens the job screen, where a
// full-width Clock in has always lived (Job.js `.fd-sticky`).
//
// See ../jobCardProps.js for the other half of the same measurement — the
// repeated date and the pill that was on every card.
//
// Below both: "Up for grabs" — jobs nobody is on, which a crew member can take
// from here rather than phoning the office. Claiming needs a live answer, so
// that one button is disabled offline and says why (actions.js claimJob).
//
// A NetworkError shows the last good bundle with an "as of" line; a refusal
// shows the server's sentence. Neither is ever a blank.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LiveCard, JobCard, Pill, PersonRow, Stat, FwButton, Notice, SectionLabel, EmptyState, Skeleton, formatClock, formatHours } from '../../../ui/fw';
import { useCan, useRole } from '../../../hooks/usePermission';
import { LIVE_CLOCK_ENDPOINT, fetchLiveClock, mayReadLiveClock, elapsedNow } from '../../shared/liveClock';
import { useSettings } from '../../../App';
import { gpsRequired } from '../gpsGate';
import { describeError, serverSentence } from '../../shared/http';
import InstallApp from '../../shared/InstallApp';
import StopSheet from '../StopSheet';
import DayEndSheet from '../DayEndSheet';
import { useResolvedNow, useOfflineQueue } from '../../shell/offline';
import { useToday, useDayEndPlan, dayEndKey, TODAY_KEY } from '../queries';
import { clockIn, clockOut, claimJob, confirmDayEnd } from '../actions';
import { jobCardProps } from '../jobCardProps';
import { timeOf, staleLabel, weekDays, compactHours } from '../fmt';
import { DAY_END_ACTIONS, appliedSentence, rememberReview, takeRemembered, forgetReview } from '../dayEnd';

export const NOCLIENT_KEY = 'fw_noclient_dismissed';
// Capped. This list is written to a phone nobody ever clears, one entry per
// job waved away for the life of the install, and it is parsed on every mount
// of the first screen of the app. What it keeps is the most RECENT dismissals:
// pruning the newest would bring a notice a crew member just dismissed
// straight back on the next load.
export const NOCLIENT_MAX = 50;

function readDismissed() {
  try {
    const parsed = JSON.parse(localStorage.getItem(NOCLIENT_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (_) { return []; }
}
function dismissNoClient(ids) {
  try {
    const add = (Array.isArray(ids) ? ids : [ids]).map(String);
    const keep = readDismissed().filter((x) => !add.includes(x));
    // The cap prunes HISTORY. It must never drop an id from the dismissal
    // being made right now: `slice(-50)` of a 60-job batch keeps the last 50
    // and throws away the first ten — which are exactly the three titles the
    // notice just printed plus the seven behind them, so the notice comes
    // straight back naming the same jobs. That could not happen while this
    // took one id at a time (1 never exceeds 50) and became reachable the
    // moment the notice was collapsed into one: workerJobs() has no LIMIT and
    // returns the whole backlog, not just today.
    const room = Math.max(NOCLIENT_MAX, add.length);
    localStorage.setItem(NOCLIENT_KEY, JSON.stringify([...keep, ...add].slice(-room)));
  } catch (_) { /* */ }
}

// ── The billing warning is ONE line (2026-09-13) ────────────────────────────
//
// This screen drew one <Notice> per clientless job. Measured in Chromium
// against the built app, on a day where eight of the jobs had no client:
//
//            notice block   "Up next"   the day's ONE Clock in   viewport
//   390px    82 -> 1072     y = 1144    y = 1260                 844
//   320px    82 -> 1367     y = 1439    y = 1555                 844
//
// The one-action rebuild directly above had just cut twelve green buttons to
// one; it does not help a crew member who cannot see it. The single action
// they open the app to press was a screenful and a half below the fold,
// behind eight identical warnings about BILLING — the office's problem, not
// the problem of somebody standing in a yard at 7am. Each also dismissed
// separately, so clearing the day cost eight taps.
//
// Three titles and then a count, because thirty-one titles is the same wall
// with fewer borders. One dismiss clears every job the notice named, and the
// ids are still remembered individually, so a ninth clientless job arriving
// tomorrow brings the notice back naming only itself.
const NOCLIENT_NAMED = 3;
export function noClientSentence(jobs) {
  const titles = jobs.map((j) => j.title).filter(Boolean);
  const named = titles.slice(0, NOCLIENT_NAMED).join(', ');
  const rest = titles.length - NOCLIENT_NAMED;
  const which = rest > 0 ? `${named} and ${rest} more` : named;
  return jobs.length === 1
    ? `${which} has no client or property. Tell the office so it can be billed.`
    : `${which} have no client or property. Tell the office so they can be billed.`;
}

// The card's content is jobCardProps(job, { today }) — title, the place, the
// short facts, and a badge only where the row is exceptional. It used to be
// built here: one grey string of place + description + estimate, with the
// status expressed only as a border colour. See ../jobCardProps.js for what
// that cost, and for why the badge slot is now spent on exceptions.

/**
 * Is this person the one you would be told to "check with"?
 *
 * navigation.js calls them DISPATCHERS and the answer matters on exactly two
 * lines below. Kept to manager|admin rather than "not a worker" on purpose:
 * `accounting` also lands here on a phone (homePathFor) and a bookkeeper is
 * not dispatch either, so the crew wording is right for them.
 */
function isDispatch(role) {
  return role === 'manager' || role === 'admin';
}

/**
 * The one job that gets the day's action — or null when none should.
 *
 * ── The order is the SERVER'S, and it is not re-sorted here ─────────────────
 *
 * `GET /api/field/today` returns `jobs` in the order `workerJobs()` selects
 * them (server/routes/field.js):
 *
 *     today first, then overdue, then unscheduled, then future;
 *     within each group by scheduled_date, then jobs.sort_order,
 *     then updated_at DESC
 *
 * There is NO time of day in any of it. The `jobs` table carries
 * `scheduled_date` — a DATE — and nothing finer, and `time_label` is that
 * date's weekday. So a "first thing this morning" ranking computed on the
 * phone would be invented, which docs/APP_REBUILD.md §6 refuses. What this
 * screen owes a crew member is that the first job in the server's order is
 * visually first and carries the action; that is the entire claim, and it is
 * one the data supports.
 *
 * ── The one refinement, and why it is not an invention ──────────────────────
 *
 * BLOCKED is skipped. `blocked` / `blocked_reason` come straight off the
 * bundle, and blocked means do not go (apps/shared/jobFields.js) — so aiming
 * the day's single Clock in at a blocked job is an instruction to drive
 * somewhere nobody can work. On a day where every job is blocked there is no
 * primary at all: the badges say why, and every card still opens.
 */
export function upNextJob(jobs) {
  return (jobs || []).find((j) => j && !j.blocked && !j.blocked_reason) || null;
}

/**
 * The crew's day, for somebody whose own day is empty because they are not on
 * the crew.
 *
 * ── The hole this fills, measured at 390x844 on 2026-09-12 ──────────────────
 *
 * `homePathFor` sends a phone-sized manager or admin here, and /field/today is
 * scoped to the CALLER. Signed in as an admin with twelve jobs running for the
 * company that day, the whole screen was:
 *
 *     YOUR JOBS · Nothing scheduled today · "The crew's day is on the Crew
 *     board." · THIS WEEK  M0 T0 W0 T0 F0 S0 S0 · 0.00 hours this week
 *
 *     844px of page in an 844px viewport — one screenful, all of it empty
 *
 * The heading and the sentence were corrected on 2026-09-10 and are right.
 * What was left is that everything under them is dead space, on the landing
 * screen, for the person most likely to be the one asking where the crew is.
 *
 * ── No new endpoint, and no new aggregate ───────────────────────────────────
 *
 * `GET /api/dispatch/now` already answers exactly this: `counts` =
 * { on_clock, late, due, finished } and `clocked_in[]` with a name, a job and
 * an elapsed per row. Read-only, and read through apps/shared/liveClock.js so
 * this is not a second reading of "who is on the clock" — the module's own
 * header explains what the second one would get wrong.
 *
 * ── The door is mirrored, not guessed ───────────────────────────────────────
 *
 * The endpoint is `requireDispatch`. A screen that asks a wider question than
 * the door polls a request the server can only answer 403 — every refetch,
 * into somebody's console, which is a bug this repo has shipped three times.
 * `mayReadLiveClock` is the composition office/screens/Overview.js already
 * uses; both useCan calls are made unconditionally because hooks may not be
 * conditional and `useCan(…) || useCan(…)` would short-circuit the second.
 *
 * `isDispatch(role)` is asked as well as the permission, and narrows rather
 * than widens: this is the crew's day drawn on the CREW's screen, and the two
 * words for it — "the crew's day is on the Crew board" — are already addressed
 * to a manager or an admin two lines above.
 *
 * ── 60 seconds, not 30 ──────────────────────────────────────────────────────
 *
 * Its own query key, not LIVE_CLOCK_KEY: dispatch/screens/Now.js polls the
 * same answer every 30s and one cache entry cannot hold two schedules. 60s
 * matches `useToday` beside it, and the reason is on the record —
 * server.js rate-limits /api/ per IP, a crew on a job site is many phones
 * behind one carrier NAT, and docs/ONE_APP.md is the badge poll that walked
 * into that limiter.
 */
function CrewDay() {
  const role = useRole();
  // Unconditional, and in this order, because hooks are.
  const approve = useCan(LIVE_CLOCK_ENDPOINT.resource, 'approve');
  const manageTeam = useCan(LIVE_CLOCK_ENDPOINT.resource, 'manage_team');
  const allowed = isDispatch(role) && mayReadLiveClock({ approve, manage_team: manageTeam });

  const q = useQuery({
    queryKey: ['field', 'today', 'crew-day'],
    queryFn: fetchLiveClock,
    enabled: allowed,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  });

  if (!allowed) return null;

  // Read as an array or as nothing — never `|| []`. "Nobody is on the clock"
  // and "we could not ask" are different facts and only the first is good
  // news; collapsing them is how a screen renders a confident wrong answer.
  const rows = q.data && Array.isArray(q.data.clocked_in) ? q.data.clocked_in : null;
  const problem = !q.isPending && !rows;
  const counts = (q.data && q.data.counts) || {};
  const received = q.data && q.data.received_at;
  const n = (v) => (v === null || v === undefined ? '—' : String(v));

  return (
    <div className="fd-section" data-section="crew-day">
      <SectionLabel>The crew’s day</SectionLabel>
      {q.isPending ? <Skeleton lines={3} /> : null}
      {problem ? (
        <Notice tone="stop">{describeError(q.error, 'Could not load the crew’s day.')}</Notice>
      ) : null}
      {rows ? (
        <>
          {/* Two columns at every phone width and four once there is room —
              see field.css .fd-crewday__stats. Nothing here is sized in px. */}
          <div className="fd-crewday__stats">
            <Stat label="On the clock" value={n(counts.on_clock)} tone={counts.on_clock ? 'now' : 'neutral'} />
            <Stat label="Late" value={n(counts.late)} tone={counts.late ? 'stop' : 'neutral'} />
            <Stat label="Not started" value={n(counts.due)} tone={counts.due ? 'warn' : 'neutral'} />
            <Stat label="Finished" value={n(counts.finished)} tone={counts.finished ? 'ok' : 'neutral'} />
          </div>
          {rows.length === 0 ? (
            // Said in words, because a row of zeros alone is the thing that
            // reads as a screen that failed to load.
            <div className="fd-hint">Nobody is on the clock right now.</div>
          ) : rows.map((r) => (
            <PersonRow
              key={r.entry_id || `${r.user_id}:${r.job_id}`}
              initials={r.initials}
              name={r.name}
              // The property then the job, in dispatch/screens/Now.js's own
              // words — the same crew member must not read one way here and
              // another way there.
              meta={[r.property_name, r.job_title].filter(Boolean).join(' · ') || 'No job'}
              tone="now"
              // H:MM and no local tick, deliberately: this panel refetches at
              // 60s, which IS the resolution of the figure shown, so a
              // per-second interval would re-render the list 59 times to
              // change nothing. The seconds hand belongs on the two screens
              // whose subject is the running clock (Now.js, Overview).
              trailing={formatClock(elapsedNow(r.elapsed_seconds, received))}
            />
          ))}
        </>
      ) : null}
    </div>
  );
}

export default function Today() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const role = useRole();
  const today = useToday();
  const now = useResolvedNow(today);
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [dismissed, setDismissed] = useState(readDismissed);
  const { online } = useOfflineQueue();
  const [stopOpen, setStopOpen] = useState(false);
  // The review a crew member is in the middle of, or null. Seeded from the
  // one-shot resume flag (dayEnd.js) so the loop — press, tap a job you
  // forgot, fix it, come back — does not end with hunting for the button a
  // second time. `takeRemembered` clears it as it reads, so this can only
  // reopen once and never on a fresh morning.
  const [review, setReview] = useState(takeRemembered);
  const [applied, setApplied] = useState(null);
  const [reviewError, setReviewError] = useState(null);
  const dayEndPlan = useDayEndPlan(review || 'day', { enabled: !!review });
  // What the location did, and what to do about it — a refusal when the
  // company requires one, an ask when it does not, a warning when the fix is
  // too wide to name a property. apps/field/gpsGate.js decides all three.
  const [gps, setGps] = useState(null);
  const { settings } = useSettings();
  const requireGps = gpsRequired(settings);

  const data = today.data;
  const jobs = (data && data.jobs) || [];
  const claimable = (data && data.claimable) || [];
  const crewLabel = (data && data.user && data.user.crew_label) || null;
  // The `week` block has ridden along on every /field/today since the rebuild
  // and nothing read it (docs/PARITY_2026-09-08.md §22). weekDays() returns []
  // when there is no block — an older cached bundle rendered offline — so the
  // section below simply does not appear rather than drawing an empty week.
  const week = (data && data.week) || null;
  const days = weekDays(week);
  const openJob = (id) => navigate(`/today/job/${id}`);

  // There was a generic `run(label, fn)` here, and Clock in was its only
  // caller. A location refusal is not a failure to report as "That did not go
  // through" — it is an instruction, with a title and a fix, and it belongs in
  // its own dismissible notice rather than the page's generic alert bar. So
  // the handler is written out, and the wrapper that had one user is gone.
  const onClockIn = async (job) => {
    setBusy(`in:${job.id}`);
    setActionError(null);
    setGps(null);
    try {
      const out = await clockIn(job, { gpsRequired: requireGps });
      if (out.sent) qc.invalidateQueries({ queryKey: TODAY_KEY });
      if (out.gps && out.gps.message) setGps(out.gps);
    } catch (e) {
      if (e && e.gps) setGps(e.gps);
      else setActionError(describeError(e, 'That did not go through'));
    } finally {
      setBusy(null);
    }
  };
  // The classic TimeTracker refuses to stop without a reason and tells every
  // admin and manager why. The rebuilt app sent note: '' and told nobody, so
  // since the cutover the office stopped hearing something it had had for
  // years. The prompt is StopSheet, shared with the job screen — which kept
  // sending no reason for a day after this was fixed here; the notification is
  // the server's job, because this op can sit in the offline queue for hours
  // (actions.js clockOut).
  //
  // Resolves the sentence to show rather than setting `actionError`: the sheet
  // stays open on a refusal so the typed reason survives it, and the page's
  // own alert bar is behind the sheet where nobody can read it.
  const onClockOut = async (reason) => {
    setBusy('out');
    setActionError(null);
    try {
      const out = await clockOut(now, { note: reason });
      if (out.sent) qc.invalidateQueries({ queryKey: TODAY_KEY });
      return null;
    } catch (e) {
      return describeError(e, 'That did not go through');
    } finally {
      setBusy(null);
    }
  };

  // Not `run`: a claim is a race, so whichever way it goes the list is now
  // wrong — the job is either ours or somebody else's. Refetch either way, so
  // a job we lost stops offering a button that cannot work.
  const onClaim = async (job) => {
    setBusy(`claim:${job.id}`);
    setActionError(null);
    try {
      await claimJob(job);
    } catch (e) {
      setActionError(describeError(e, 'Could not claim that job'));
    } finally {
      qc.invalidateQueries({ queryKey: TODAY_KEY });
      setBusy(null);
    }
  };

  // ── End of day / end of week ──────────────────────────────────────────────
  //
  // Which buttons exist is the SERVER's answer (`day_end` on /field/today):
  // whether this crew member has jobs scheduled today, and whether today is
  // the last day of the company week. The phone must not work out whether it
  // is Sunday in Connecticut — that is a question about `start_of_week` and
  // the company timezone, and server/utils/dayEnd.js answers it.
  const dayEnd = (data && data.day_end) || null;
  const canReview = (scope) => {
    if (!dayEnd) return false;
    return scope === 'week' ? !!dayEnd.week_available : (dayEnd.day_jobs || 0) > 0;
  };

  const openReview = (scope) => {
    setApplied(null);
    setReviewError(null);
    setReview(scope);
  };
  const closeReview = () => {
    forgetReview();
    setReview(null);
    setApplied(null);
    setReviewError(null);
  };
  // Tapping a job in the review leaves for that job — and says "come back
  // here" on the way out, which is the whole of what makes the loop cheap.
  const openReviewJob = (job) => {
    rememberReview(review);
    setReview(null);
    openJob(job.id);
  };

  const confirmReview = async () => {
    const plan = dayEndPlan.data;
    if (!plan) return;
    setBusy('day-end');
    setReviewError(null);
    try {
      const out = await confirmDayEnd(review, plan.signature);
      setApplied(appliedSentence(out));
      // The server sent the review AFTER its own writes. Showing that rather
      // than refetching means the sheet cannot flash a plan that is one
      // request out of date.
      if (out && out.plan) qc.setQueryData(dayEndKey(review), out.plan);
      qc.invalidateQueries({ queryKey: TODAY_KEY });
    } catch (e) {
      // A 409 is the review again: the server refused BECAUSE what it found
      // no longer matches what was on screen, and its body carries the fresh
      // plan. Draw that, say why, and let them press again — the alternative
      // is a sheet insisting on a promise the database has already broken.
      const fresh = e && e.body && e.body.plan;
      if (fresh) qc.setQueryData(dayEndKey(review), fresh);
      setApplied(null);
      setReviewError(serverSentence(e, 'That did not go through'));
    } finally {
      setBusy(null);
    }
  };

  const noClientJobs = jobs.filter((j) => j.no_client && !dismissed.includes(String(j.id)));
  const nextUp = now ? jobs.filter((j) => j.id !== now.job_id) : jobs;
  // Null while a shift is running: the one action on that screen is Clock out,
  // on the LiveCard above, and a second loud green button under it would put
  // the day back to two primaries — the thing this screen was rebuilt to stop.
  const upNext = now ? null : upNextJob(nextUp);
  const rest = nextUp.filter((j) => j !== upNext);
  // The COMPANY's today, off the bundle (server/utils/dateRange.js). Never
  // computed here: the browser's idea of the date is the device's timezone,
  // and this app is used on phones that travel. jobCardProps uses it for one
  // thing — dropping a date column that repeats the day already on the screen.
  const cardProps = (job) => jobCardProps(job, { today: data && data.today });

  let body;
  if (today.isPending && !data) {
    body = <Skeleton lines={5} />;
  } else if (today.error && !data) {
    body = (
      <Notice tone="stop" title="Could not load today">
        {describeError(today.error)}{' '}
        <FwButton variant="quiet" onClick={() => today.refetch()}>Try again</FwButton>
      </Notice>
    );
  } else {
    body = (
      <>
        {data && data.__stale ? <div className="fd-stale">No signal — showing your list {staleLabel(data.__stale)}</div> : null}
        {actionError ? <Notice tone="stop" onDismiss={() => setActionError(null)}>{actionError}</Notice> : null}
        {gps ? <Notice tone={gps.tone} title={gps.title} onDismiss={() => setGps(null)}>{gps.message}</Notice> : null}
        {now && now.warning ? <Notice tone="warn">{now.warning}</Notice> : null}
        {noClientJobs.length > 0 ? (
          <Notice
            tone="warn"
            title={noClientJobs.length === 1 ? 'No client on this job' : `No client on ${noClientJobs.length} jobs`}
            onDismiss={() => { dismissNoClient(noClientJobs.map((j) => j.id)); setDismissed(readDismissed()); }}
          >
            {noClientSentence(noClientJobs)}
          </Notice>
        ) : null}

        {now ? (
          <div className="fd-section">
            <LiveCard
              pills={(
                <>
                  <Pill tone="now">On the clock</Pill>
                  {now.queued ? <Pill tone="sched">Queued</Pill> : null}
                  {now.subtask_total > 0 ? <Pill tone="ok">{now.subtask_done} of {now.subtask_total} done</Pill> : null}
                </>
              )}
              title={now.job_title || now.property_name || 'On the clock'}
              subtitle={now.address || now.property_name || now.client_name || null}
              startedAt={now.started_at}
              sinceLabel={now.started_at ? `Started ${timeOf(now.started_at)}` : null}
              action={<FwButton variant="stop" size="lg" full onClick={() => setStopOpen(true)} loading={busy === 'out'}>Clock out</FwButton>}
            />
            {now.job_id ? (
              <FwButton variant="ghost" icon="list" full onClick={() => openJob(now.job_id)}>Work list</FwButton>
            ) : null}
          </div>
        ) : null}

        <div className="fd-section">
          {/*
            "Your jobs" for a dispatcher, and it is not a nicety.

            /field/today is scoped to the CALLER (server/routes/field.js), and
            homePathFor sends a phone-sized manager straight here. Measured at
            390px on 2026-09-10: a manager with two jobs scheduled for the crew
            that day, and none assigned to them personally, was shown "Today"
            over an empty list — an empty company day, on the landing screen,
            which is a plausible-looking wrong answer rather than a broken one.
            One word says whose list it is, in the full case as well as the
            empty one. A crew member keeps "Today": everything on their screen
            is theirs by definition.
          */}
          <SectionLabel>{now ? 'Next up' : (isDispatch(role) ? 'Your jobs' : 'Today')}</SectionLabel>
          {/* "With Dana Whitfield" — who this crew member is working under
              today. server/routes/field.js:308 has computed `crew_label` on
              every one of these requests and nothing drew it
              (docs/PARITY_2026-09-08.md §8), so a crew member could not tell
              from their own day that they were somebody's second on it. The
              server sends null when they are the lead, or alone, so this line
              only ever appears when it says something. */}
          {crewLabel ? <div className="fd-hint">{crewLabel}</div> : null}
          {nextUp.length === 0 ? (
            <EmptyState icon="calendar" title={now ? 'Nothing else today' : 'Nothing scheduled today'}>
              {/*
                "Check with dispatch" was shown to the manager who IS dispatch
                — advice to go and ask themselves — and it was the only thing
                on their landing screen. A dispatcher gets told where the
                company's day actually is instead, in the words navigation.js
                uses for that row, so the sentence and the menu agree.
              */}
              {now
                ? 'Clock out when you are done.'
                : (isDispatch(role)
                  ? 'Nothing is assigned to you today. The crew’s day is on the Crew board.'
                  : 'Check with dispatch if that looks wrong.')}
            </EmptyState>
          ) : (
            <>
              {/* ── The one job with the action on it ──────────────────────
                  Labelled in WORDS, not by position alone: on a list of cards
                  that all look alike, "the first one" is not something a
                  person reads — it is something they work out. `Up next` is
                  two words and it is the answer to the only question a crew
                  member opens this screen with. */}
              {upNext ? (
                <div className="fd-up" data-fd-up={upNext.id}>
                  <div className="fd-up__label">Up next</div>
                  <JobCard
                    {...cardProps(upNext)}
                    onClick={() => openJob(upNext.id)}
                  />
                  {/* `full` is the honest declaration: this button IS the full
                      width of its block, and a check asking "does any control
                      eat the screen" has to be able to tell a deliberate
                      full-width action from a button that grew until it took
                      half the phone — which is the defect this row was rebuilt
                      to fix (ui-check screenHoggingControls reads the class).
                      It stays full width at EVERY width here, unlike
                      .fd-jobrow: there is one of it on the screen, and the one
                      action on a phone screen is the easy thing to hit. */}
                  <FwButton
                    variant="go"
                    size="lg"
                    full
                    icon="play"
                    onClick={() => onClockIn(upNext)}
                    loading={busy === `in:${upNext.id}`}
                    disabled={busy !== null && busy !== `in:${upNext.id}`}
                    aria-label={`Clock in to ${upNext.title}`}
                  >
                    Clock in
                  </FwButton>
                </div>
              ) : null}
              {/* Everything else: the same quiet tappable card the on-clock
                  branch has always drawn. The whole card is a <button>
                  (ui/fw/components/JobCard.js), it opens the job, and the job
                  screen's sticky Clock in is one tap away — so no job has lost
                  its action, only its twelfth full-width green button. */}
              {rest.map((job) => (
                <JobCard
                  key={job.id}
                  data-fd-quiet={job.id}
                  {...cardProps(job)}
                  onClick={() => openJob(job.id)}
                />
              ))}
            </>
          )}
        </div>

        {/* Directly under the empty list it explains, and only there. A
            manager who IS on a job today gets the crew screen unchanged —
            this goes in the hole, it does not compete with their own work.
            CrewDay answers `null` for anyone the server's own door would
            refuse, and asks for nothing in that case. */}
        {!now && nextUp.length === 0 ? <CrewDay /> : null}

        {/* Closing out. Directly under the day's jobs, because it acts on
            exactly that list and because that is where a crew member is
            looking at 5pm — not buried below "Up for grabs", which is about
            taking MORE work on. Both buttons are `full`: at 320px a button
            beside another button is two unreadable buttons, and `full` is the
            honest declaration a layout check can tell from a control that
            grew until it ate the screen (see .fd-jobrow above). */}
        {DAY_END_ACTIONS.some((a) => canReview(a.scope)) ? (
          <div className="fd-section" data-section="day-end">
            <SectionLabel>Closing out</SectionLabel>
            {DAY_END_ACTIONS.filter((a) => canReview(a.scope)).map((a) => (
              <FwButton
                key={a.scope}
                variant={a.variant}
                size="lg"
                full
                icon={a.icon}
                onClick={() => openReview(a.scope)}
                disabled={busy !== null}
                aria-label={a.label}
                data-day-end={a.scope}
              >
                {a.label}
              </FwButton>
            ))}
            {/* Said once, here, rather than discovered by pressing: nothing
                happens until the review has been looked at and confirmed. */}
            <div className="fd-hint">
              {dayEnd && dayEnd.week_available
                ? 'You will see every job that gets marked done, and which leftover items move to the next day, before anything happens.'
                : `You will see every job that gets marked done, and which leftover items move to the next day, before anything happens. End of week opens on ${(dayEnd && dayEnd.week_last_day_label) || 'the last day of the week'}.`}
            </div>
          </div>
        ) : null}

        <StopSheet
          open={stopOpen}
          busy={busy === 'out'}
          onClose={() => setStopOpen(false)}
          onStop={onClockOut}
        />

        <DayEndSheet
          open={!!review}
          scope={review}
          plan={dayEndPlan.data}
          loading={dayEndPlan.isPending}
          error={reviewError || (dayEndPlan.error ? describeError(dayEndPlan.error) : null)}
          applied={applied}
          busy={busy === 'day-end'}
          online={online}
          onOpenJob={openReviewJob}
          onConfirm={confirmReview}
          onClose={closeReview}
          onRetry={() => dayEndPlan.refetch()}
        />

        {claimable.length > 0 ? (
          <div className="fd-section">
            <SectionLabel>Up for grabs</SectionLabel>
            {!online ? (
              <Notice tone="warn">
                Claiming needs a connection — nobody can hold a job for you offline.
              </Notice>
            ) : null}
            {/* One Claim per job, deliberately kept — this is NOT the
                twelve-Clock-in shape above. A claim is not exclusive: taking
                one job does not decide the others, so N jobs here really are
                N independent decisions, and the list is what dispatch has not
                handed out rather than a whole day's work. The cards read the
                same way as the day's, `today` and all. */}
            {claimable.map((job) => (
              <div key={job.id} className="fd-jobrow">
                <JobCard
                  {...cardProps(job)}
                  onClick={() => openJob(job.id)}
                />
                <FwButton
                  variant="go"
                  size="lg"
                  full
                  icon="plus"
                  onClick={() => onClaim(job)}
                  loading={busy === `claim:${job.id}`}
                  disabled={!online || (busy !== null && busy !== `claim:${job.id}`)}
                  aria-label={`Claim ${job.title}`}
                >
                  Claim
                </FwButton>
              </div>
            ))}
          </div>
        ) : null}

        {/* The shape of the week, from `week.hours_by_day` — seven cells, not
            one per reported day: the server only sends days that HAVE clock
            lines, so iterating the object would draw a two-day "week".
            Informational, so it sits below everything a crew member can act
            on and above the install offer. */}
        {days.length > 0 ? (
          <div className="fd-section">
            <SectionLabel>This week</SectionLabel>
            <div className="fd-week">
              {days.map((d) => (
                <div
                  key={d.date}
                  className="fd-week__day"
                  data-fd-weekday={d.date}
                  data-worked={d.worked ? 'true' : 'false'}
                >
                  <span className="fd-week__letter" aria-hidden="true">{d.label}</span>
                  <span className="fd-week__hours">{compactHours(d.hours)}</span>
                </div>
              ))}
            </div>
            {/* The total keeps its real precision — formatHours, not
                compactHours — because this line has the room the cells do
                not, and a week total is a number somebody checks against
                their own count. */}
            <div className="fd-hint">{formatHours(week.total_hours)} hours this week</div>
          </div>
        ) : null}

        {/* The install offer, once, at the END of the day's list — never
            between a crew member and a Clock in button, and never pinned over
            anything (see shared/InstallApp: it is in flow, with no z-index).
            Dismissible here, and dismissing it is permanent; Me carries the
            same offer undismissible for anyone who wants it later. */}
        <div className="fd-section">
          <InstallApp dismissible />
        </div>
      </>
    );
  }

  return body;
}
